"use client";

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

interface AnimatedBackgroundProps {
  className?: string;
}

/**
 * AnimatedBackground Component
 * Provides a futuristic AI-themed background with floating particles and network effects.
 * Designed for high-performance SaaS hero sections with a luxury tech aesthetic.
 * Uses Framer Motion for smooth, hardware-accelerated animations.
 */
export function AnimatedBackground({ className = "" }: AnimatedBackgroundProps) {
  // Generate random data for particles once to prevent re-renders and ensure consistency
  const particles = useMemo(() => {
    return Array.from({ length: 45 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 1,
      duration: Math.random() * 12 + 8,
      delay: Math.random() * -20,
      // Brand palette colors from design system: Primary (Neon Purple), Accent (Deep Blue), and White
      color: i % 4 === 0 ? "var(--primary)" : i % 4 === 1 ? "var(--accent)" : "#ffffff",
    }));
  }, []);

  // Generate random lines for the "digital network" effect
  const connections = useMemo(() => {
    return Array.from({ length: 12 }).map((_, i) => ({
      id: i,
      x1: Math.random() * 100,
      y1: Math.random() * 100,
      x2: Math.random() * 100,
      y2: Math.random() * 100,
      duration: Math.random() * 8 + 12,
    }));
  }, []);

  return (
    <div className={`absolute inset-0 -z-10 overflow-hidden bg-background pointer-events-none ${className}`}>
      {/* Ambient Gradient Glows - Creates depth and luxury brand positioning */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            opacity: [0.15, 0.3, 0.15],
            scale: [1, 1.15, 1],
            x: [0, 40, 0],
            y: [0, 20, 0],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[-15%] left-[-10%] w-[70%] h-[70%] bg-primary/20 blur-[120px] rounded-full"
        />
        <motion.div
          animate={{
            opacity: [0.1, 0.25, 0.1],
            scale: [1.2, 1, 1.2],
            x: [0, -30, 0],
            y: [0, -40, 0],
          }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-[-15%] right-[-10%] w-[70%] h-[70%] bg-accent/20 blur-[140px] rounded-full"
        />
      </div>

      {/* Digital Grid - Adds structure and tech vibe */}
      <div 
        className="absolute inset-0 opacity-[0.04]"
        style={{ 
          backgroundImage: `radial-gradient(circle at 1px 1px, var(--foreground) 1px, transparent 0)`,
          backgroundSize: '40px 40px' 
        }} 
      />

      {/* Network Effect - Animating paths between data nodes */}
      <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--primary)" stopOpacity="0" />
            <stop offset="50%" stopColor="var(--primary)" stopOpacity="0.4" />
            <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {connections.map((line) => (
          <motion.line
            key={`line-${line.id}`}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke="url(#line-gradient)"
            strokeWidth="0.08"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ 
              pathLength: [0, 1, 0], 
              opacity: [0, 0.4, 0],
              x1: [line.x1, line.x1 + (Math.random() - 0.5) * 4, line.x1],
              y1: [line.y1, line.y1 + (Math.random() - 0.5) * 4, line.y1]
            }}
            transition={{
              duration: line.duration,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        ))}
      </svg>

      {/* AI Particles Layer - Floating glowing nodes */}
      <div className="absolute inset-0">
        {particles.map((p) => (
          <motion.div
            key={`particle-${p.id}`}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.size,
              height: p.size,
              backgroundColor: p.color,
              boxShadow: `0 0 ${p.size * 3}px ${p.color}`,
            }}
            animate={{
              y: [0, -140, 0],
              x: [0, (Math.random() - 0.5) * 30, 0],
              opacity: [0, 0.6, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: p.duration,
              repeat: Infinity,
              delay: p.delay,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Scanning Sweep Effect - Minimal tech interaction */}
      <motion.div
        animate={{
          top: ['-10%', '110%'],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear",
        }}
        className="absolute left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/20 to-transparent opacity-30"
      />
    </div>
  );
}
